import React, { useCallback, useEffect, useMemo } from 'react';
import {
  Button,
  Checkbox,
  Col,
  Divider,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Tooltip,
} from 'antd';
import { useTranslation } from 'react-i18next';
import useSwr from '@hooks/useSwr';
import { InfoCircleOutlined } from '@ant-design/icons';

type FlagType = 'S' | 'N';
export type UnitType = 'KB' | 'MB' | 'GB';
export type UnitsType = {
  franchise: UnitType;
  execPrice: UnitType;
};

export interface IConfigStepValues {
  planDesc: string | undefined;
  externalId: string | undefined;
  price: number | undefined;
  activationPrice: number | undefined;
  franchise: number | undefined;
  sharedFranchise: number | undefined;
  megabyteExcPrice: number | undefined;
  loyaltyType: number | undefined;
  loyaltyTime: number | undefined;
  penalty: number | undefined;
  automaticBlocking: boolean | undefined;
  motOperator: string | undefined;
  isca: boolean | undefined;
  iscaDurationInDays: number | undefined;
  iscaMaxRenewal: number | undefined;
  planType: 'STANDARD' | 'BAIT';
}

const formInitialValues = {
  planDesc: undefined,
  externalId: undefined,
  price: 0,
  activationPrice: 0,
  franchise: 0,
  sharedFranchise: 0,
  megabyteExcPrice: 0,
  loyaltyType: 0,
  loyaltyTime: undefined,
  penalty: 0,
  automaticBlocking: false,
};

interface IConfigStep {
  values: IConfigStepValues | undefined;
  chargedByUse: FlagType | undefined;
  sharingType: number | undefined;
  onChange: (values: IConfigStepValues) => void;
  units: UnitsType;
  setUnits: (units: UnitsType) => void;
}

const ConfigStep = ({
  values,
  chargedByUse,
  sharingType,
  onChange,
  units,
  setUnits,
}: IConfigStep) => {
  const { t } = useTranslation(['contracts', 'attributes']);
  const [form] = Form.useForm();
  const loyaltyTypeValue = Form.useWatch('loyaltyType', form);
  const planType = Form.useWatch('planType', form);

  const { data: operatorsData } = useSwr('/service-proxy/mot-operators');

  const operatorsOptions = useMemo(
    () =>
      operatorsData?.map(({ name }: { name: string }) => ({
        label: name,
        value: name,
      })),
    [operatorsData],
  );

  useEffect(() => {
    if (values) {
      form.setFieldsValue(values);
    }
  }, [form, values]);

  const loyaltyTypeOptions = useMemo(
    () =>
      ['noPenalty', 'fixedPenalty', 'penaltyOnContract']?.map(
        (type, index) => ({
          label: t(`attributes:typesOfPenalty.${type}`),
          value: index,
        }),
      ),
    [t],
  );

  const handleOnValuesChange = useCallback(
    (changedValue: any, allValues: IConfigStepValues) => {
      onChange({ ...formInitialValues, ...allValues });
    },
    [onChange],
  );

  return (
    <Form
      form={form}
      onValuesChange={handleOnValuesChange}
      initialValues={formInitialValues}
      layout="vertical"
    >
      <Row gutter={[16, 10]} style={{ marginBottom: 10 }}>
        <Col lg={12} xl={12} xs={24}>
          <Form.Item
            required
            name="planDesc"
            label={t('attributes:description')}
          >
            <Input allowClear />
          </Form.Item>
        </Col>

        {chargedByUse === 'N' && (
          <Col lg={12} xl={12} xs={24}>
            <Form.Item
              required
              name="motOperator"
              label={t('attributes:vendor')}
            >
              <Select allowClear showSearch options={operatorsOptions} />
            </Form.Item>
          </Col>
        )}
        <Col lg={12} xl={12} xs={24}>
          <Form.Item name="externalId" label={t('attributes:externalId')}>
            <Input allowClear />
          </Form.Item>
        </Col>
      </Row>
      <Divider
        style={{
          marginTop: 40,
          fontSize: '0.85rem',
          opacity: 0.7,
          textTransform: 'uppercase',
        }}
      >
        {t('details.billingModel')}
      </Divider>
      <Row gutter={[16, 10]} style={{ marginBottom: 10 }}>
        <Col lg={12} xl={12} xs={24}>
          <Form.Item name="price" label={t('attributes:price')}>
            <InputNumber
              min={0}
              step={0.01}
              addonBefore="R$"
              style={{ width: '100%' }}
            />
          </Form.Item>
        </Col>

        <Col lg={12} xl={12} xs={24}>
          <Form.Item
            name="activationPrice"
            label={t('attributes:activationPrice')}
          >
            <InputNumber
              addonBefore="R$"
              min={0}
              step={0.01}
              style={{ width: '100%' }}
            />
          </Form.Item>
        </Col>
        <Col lg={12} xl={12} xs={24}>
          <Form.Item name="franchise" label={t('attributes:franchise')}>
            <InputNumber
              addonAfter={
                <Select
                  value={units?.franchise}
                  onChange={value => setUnits({ ...units, franchise: value })}
                  options={['KB', 'MB', 'GB'].map(item => ({
                    label: item,
                    value: item,
                  }))}
                  style={{ width: 64 }}
                />
              }
              min={0}
              style={{ width: '100%' }}
            />
          </Form.Item>
        </Col>
        {sharingType === 3 && (
          <Col lg={12} xl={12} xs={24}>
            <Form.Item
              name="sharedFranchise"
              label={t('attributes:sharedFranchise')}
            >
              <InputNumber min={0} style={{ width: '100%' }} addonAfter="MB" />
            </Form.Item>
          </Col>
        )}
        {chargedByUse === 'N' && (
          <Col lg={12} xl={12} xs={24}>
            <Form.Item
              name="megabyteExcPrice"
              label={t('attributes:megabyteExcPrice')}
            >
              <InputNumber
                min={0}
                step={0.01}
                style={{ width: '100%' }}
                addonBefore="R$"
                addonAfter={
                  <Select
                    value={units?.execPrice}
                    onChange={value => setUnits({ ...units, execPrice: value })}
                    options={['KB', 'MB', 'GB'].map(item => ({
                      label: item,
                      value: item,
                    }))}
                    style={{ width: 64 }}
                  />
                }
              />
            </Form.Item>
          </Col>
        )}

        <Col lg={12} xl={12} xs={24}>
          <Form.Item required name="planType" label={t('attributes:planType')}>
            <Select style={{ width: '100%' }}>
              <Select.Option value="STANDARD">
                {t('attributes:planTypes.standard')}
              </Select.Option>
              <Select.Option value="BAIT">
                {t('attributes:planTypes.bait')}
              </Select.Option>
            </Select>
          </Form.Item>
        </Col>

        {planType === 'BAIT' && (
          <>
            <Col lg={12} xl={12} xs={24}>
              <Form.Item
                required
                name="iscaDurationInDays"
                label={t('attributes:iscaDurationInDays')}
              >
                <Select style={{ width: '100%' }} allowClear>
                  {[30, 60, 90].map(item => (
                    <Select.Option value={item} key={item}>
                      {item}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col lg={12} xl={12} xs={24}>
              <Form.Item
                name="iscaMaxRenewal"
                label={t('attributes:iscaMaxRenewal')}
              >
                <InputNumber min={0} step={1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </>
        )}

        {planType === 'ALLOWANCE' && (
          <Col lg={12} xl={12} xs={24}>
            <Form.Item
              required
              name="planExpirationMonths"
              label={t('attributes:planExpirationMonths')}
            >
              <Select style={{ width: '100%' }}>
                <Select.Option value={12}>
                  {`12 ${t('attributes:months')}`}
                </Select.Option>
                <Select.Option value={24}>
                  {`24 ${t('attributes:months')}`}
                </Select.Option>
                <Select.Option value={36}>
                  {`36 ${t('attributes:months')}`}
                </Select.Option>
              </Select>
            </Form.Item>
          </Col>
        )}

        {planType !== 'ALLOWANCE' && (
          <>
            {planType !== 'BAIT' && (
              <Col lg={12} xl={12} xs={24}>
                <Form.Item
                  name="loyaltyType"
                  label={t('attributes:penaltyForCancellation')}
                >
                  <Select
                    options={loyaltyTypeOptions}
                    style={{ width: '100%' }}
                    allowClear
                    showSearch
                  />
                </Form.Item>
              </Col>
            )}
            {loyaltyTypeValue === 1 && (
              <Col lg={12} xl={12} xs={24}>
                <Form.Item name="penalty" label={t('attributes:penaltyAmount')}>
                  <InputNumber
                    addonBefore="R$"
                    min={0}
                    step={0.01}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
            )}
            {loyaltyTypeValue === 2 && (
              <Col lg={12} xl={12} xs={24}>
                <Form.Item
                  required
                  name="loyaltyTime"
                  label={t('attributes:loyaltyTime')}
                >
                  <Select style={{ width: '100%' }}>
                    {[12, 24, 36].map(item => (
                      <Select.Option value={item} key={item}>
                        {item}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            )}
          </>
        )}
      </Row>

      <Row gutter={[16, 16]}>
        {planType === 'ALLOWANCE' && (
          <Col sm={12} xs={24}>
            <Form.Item required name="automaticRenewal" valuePropName="checked">
              <Checkbox>{t('attributes:automaticRenewal')}</Checkbox>
            </Form.Item>
          </Col>
        )}
        {planType === 'BAIT' && (
          <Col sm={12} xs={24}>
            <Form.Item name="chargeIscaOnTransfer" valuePropName="checked">
              <Checkbox>
                {t('attributes:chargeIscaOnTransfer')}
                <Tooltip title={t('attributes:chargeIscaOnTransferInfo')}>
                  <Button
                    type="link"
                    size="small"
                    icon={<InfoCircleOutlined />}
                  />
                </Tooltip>
              </Checkbox>
            </Form.Item>
          </Col>
        )}
        <Col sm={12} xs={24}>
          <Form.Item name="automaticBlocking" valuePropName="checked">
            <Checkbox>{t('attributes:automaticBlocking.title')}</Checkbox>
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
};

export default ConfigStep;
